import { Alert, Box, CircularProgress, Chip, Typography } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from 'axios';
import React, { useContext, useEffect, useState } from 'react';
import { API_ENDPOINT } from '../../../configurations/global.config';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { CriticalOrderBalance } from '../../../interfaces/delivery-company/orders';
import { displayMoney } from '../../../utils/money';

interface CriticalOrderBalanceDisplayProps {
  compact?: boolean; // Compact display mode
  balance?: CriticalOrderBalance;
  loading: boolean;
}

function CriticalOrderBalanceDisplay({ compact = false, balance, loading }: CriticalOrderBalanceDisplayProps) {


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!balance) return null;

  const percentUsed = balance.effective_monthly_credit > 0 
    ? (balance.current_balance / balance.effective_monthly_credit) * 100 
    : 100;

  // If monthly credit is 0, only show surcharge information (no balance/credit info)
  if (balance.effective_monthly_credit === 0) {
    if (compact) {
      return (
        <Box sx={{ mt: 1 }}>
          {balance.will_incur_surcharge && (
            <Typography variant="caption" color="warning.main" display="block">
              Additional charge: {displayMoney(balance.estimated_surcharge)}
            </Typography>
          )}
        </Box>
      );
    }

    return (
      <Alert
        severity="warning"
        icon={<WarningIcon />}
        sx={{ mt: 2, mb: 2 }}
      >
        <Typography variant="subtitle2" gutterBottom>
          Critical Order Surcharge
        </Typography>
        
        <Alert severity="warning" sx={{ mt: 1 }}>
          <Typography variant="body2">
            <strong>Surcharge applies to all critical orders!</strong>
            <br />
            This order will incur an additional charge of <strong>{displayMoney(balance.estimated_surcharge)}</strong>.
          </Typography>
        </Alert>
      </Alert>
    );
  }

  if (compact) {
    return (
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Critical orders this month: {balance.current_balance} / {balance.effective_monthly_credit}
        </Typography>
        {balance.will_incur_surcharge && balance && (
          <Typography variant="caption" color="warning.main" display="block">
            Additional charge: {displayMoney(balance.estimated_surcharge)}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Alert
      severity={balance.will_incur_surcharge ? 'warning' : percentUsed > 80 ? 'info' : 'success'}
      icon={balance.will_incur_surcharge ? <WarningIcon /> : <CheckCircleIcon />}
    > 
      {!(balance.will_incur_surcharge && balance.estimated_surcharge) && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="body2">
          <strong>{balance.current_balance}</strong> of <strong>{balance.effective_monthly_credit}</strong> critical orders used this month
        </Typography>
        <Chip
          label={`${balance.remaining_credit} remaining`}
          size="small"
          color={balance.will_incur_surcharge ? 'warning' : 'success'}
        />
      </Box>}

      {(balance.will_incur_surcharge && balance.estimated_surcharge) ? (
          <Typography variant="body2">
            <strong>Extra charges will apply!</strong>
            <br />
            This order will incur an additional charge of <strong>{displayMoney(balance.estimated_surcharge)}</strong> {balance.effective_monthly_credit > 0 && <>because you have exceeded your monthly critical order limit.</>}
          </Typography>
      ) :  <Typography  variant="body2">No extra fees applied</Typography>}

      {!balance.will_incur_surcharge && percentUsed > 80 && (
        <Typography variant="caption" color="text.secondary">
          You are approaching your monthly limit. After {balance.remaining_credit} more critical orders, additional charges will apply.
        </Typography>
      )}
    </Alert>
  );
}

export default CriticalOrderBalanceDisplay;

